import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  FileText,
  Download,
  Check,
  Loader2,
  Compass,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};
const staggerContainer = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };

/* ─── Guide Data ─── */
const guides = [
  { slug: "neurodivergent-adults", title: "Understanding Late-Diagnosed Neurodivergence", audience: "For AuDHD Adults", description: "A guide for adults navigating a late diagnosis — making sense of identity, executive functioning, and finding support that actually fits." },
  { slug: "parents-carers", title: "Navigating Neurodivergence as a Family", audience: "For Parents & Carers", description: "Practical guidance for parents coordinating schools, providers, and family life — without losing yourself in the process." },
  { slug: "complex-trauma", title: "Finding Coherence in Fragmented Care", audience: "For Adults in Complex Systems", description: "For those navigating multiple services and life stressors — understanding what integrated support looks like and how to access it." },
  { slug: "young-adults", title: "When Understanding Isn't Enough", audience: "For Young Adults", description: "A resource for young people dealing with emotional exhaustion, overwhelm, and the gap between knowing what's wrong and knowing what to do." },
];

/* ─── Guide Card with Email Gate ─── */
function GuideCard({ guide }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("submitting");
    setError("");
    try {
      const res = await axios.post(`${API}/guide-download`, { email: email.trim(), guide_slug: guide.slug });
      if (res.data.download_url) {
        window.open(res.data.download_url, "_blank");
        setState("success");
      } else {
        setState("unavailable");
      }
      setEmail("");
    } catch {
      setError("Something went wrong. Please try again.");
      setState("idle");
    }
  };

  return (
    <div className="glass-card p-7 flex flex-col" data-testid={`guide-card-${guide.slug}`}>
      <div className="w-10 h-10 rounded-2xl bg-[#4A6741]/10 flex items-center justify-center mb-5">
        <FileText size={18} className="text-[#4A6741]" strokeWidth={1.5} />
      </div>
      <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#4A6741] mb-2">{guide.audience}</p>
      <h3 className="font-['Cormorant_Garamond'] text-xl font-normal text-[#1A1C19] mb-2 leading-tight">{guide.title}</h3>
      <p className="text-xs font-light text-[#5C635A] leading-relaxed mb-5 flex-1">{guide.description}</p>

      {state === "success" ? (
        <div className="flex items-center gap-2 text-[#4A6741]"><Check size={14} /><span className="text-xs font-medium">Guide sent — check your download</span></div>
      ) : state === "unavailable" ? (
        <span className="text-xs font-light text-[#5C635A]">This guide is coming soon. We've saved your email and will notify you when it's ready.</span>
      ) : (
        <form onSubmit={handleSubmit} className="flex gap-2">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" required className="flex-1 px-3 py-2 rounded-lg bg-[#F7F5F0] border border-transparent focus:border-[#4A6741]/30 text-xs font-light text-[#1A1C19] placeholder:text-[#5C635A]/50 outline-none transition-colors min-w-0" data-testid={`guide-email-${guide.slug}`} />
          <button type="submit" disabled={state === "submitting"} className="w-9 h-9 rounded-lg bg-[#4A6741] text-white flex items-center justify-center hover:bg-[#2C4027] transition-colors disabled:opacity-40 shrink-0" data-testid={`guide-download-btn-${guide.slug}`}>
            {state === "submitting" ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
          </button>
        </form>
      )}
      {error && <p className="text-red-600 text-[10px] mt-2">{error}</p>}
    </div>
  );
}

/* ─── Resources Page ─── */
export default function ResourcesPage() {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Resources | ALIGN&bull;</title>
        <meta name="description" content="Free guides, tools, and workbooks from ALIGN•. Practical resources for parents, caregivers, neurodivergent adults, and young people." />
      </Helmet>

      <div className="min-h-screen" style={{ backgroundColor: "#F7F5F0" }} data-testid="resources-page">
        {/* Header */}
        <header className="bg-white/70 backdrop-blur-xl border-b border-white/20 sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between h-20">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate("/")} className="text-[#5C635A] hover:text-[#4A6741] transition-colors">
                <ArrowLeft size={18} />
              </button>
              <button onClick={() => navigate("/")} className="font-['Cormorant_Garamond'] text-2xl font-light tracking-tight text-[#1A1C19] hover:text-[#4A6741] transition-colors">
                ALIGN<span className="text-[#4A6741]">&bull;</span>
              </button>
            </div>
            <p className="text-[#5C635A] font-['Manrope'] text-xs uppercase tracking-widest font-medium">Resources</p>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {/* Page Header */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center py-16 md:py-24 pb-8 md:pb-12">
            <p className="text-xs uppercase tracking-[0.3em] font-medium text-[#4A6741] mb-6">Resources</p>
            <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light tracking-tight text-[#1A1C19] mb-6">Free guides &amp; tools</h1>
            <p className="text-lg font-light text-[#5C635A] max-w-2xl mx-auto">
              Practical, research-backed resources tailored to the people we serve — for parents, caregivers, neurodivergent adults, and young people.
            </p>
          </motion.div>

          {/* ─── Section 1: Free Guides ─── */}
          <section className="py-12 md:py-16">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-12">
              <p className="text-xs uppercase tracking-[0.3em] font-medium text-[#4A6741] mb-4">Guides</p>
              <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-light tracking-tight text-[#1A1C19] mb-4">Free guides</h2>
              <p className="text-sm font-light text-[#5C635A] max-w-xl mx-auto">
                Enter your email to download — we'll never spam you.
              </p>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {guides.map((guide) => (
                <motion.div key={guide.slug} variants={fadeUp}>
                  <GuideCard guide={guide} />
                </motion.div>
              ))}
            </motion.div>
          </section>

          {/* ─── Divider ─── */}
          <div className="flex justify-center py-4">
            <div className="section-divider" />
          </div>

          {/* ─── Section 2: Free Tools ─── */}
          <section className="py-12 md:py-16 pb-24 md:pb-32">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-12">
              <p className="text-xs uppercase tracking-[0.3em] font-medium text-[#4A6741] mb-4">Free Tools</p>
              <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-light tracking-tight text-[#1A1C19] mb-4">Practical tools for parents</h2>
              <p className="text-sm font-light text-[#5C635A] max-w-xl mx-auto">
                Free, research-backed resources you can use at home — guides, trackers, and workbooks designed for real family life.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {/* Tool 1: Messy Rooms */}
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="glass-card p-7 flex flex-col" data-testid="free-tool-messy-rooms">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-2xl bg-[#4A6741]/10 flex items-center justify-center shrink-0">
                    <FileText size={18} className="text-[#4A6741]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#4A6741] mb-1">Guide + Tracker</p>
                    <h4 className="font-['Cormorant_Garamond'] text-xl font-normal text-[#1A1C19] leading-tight">Messy Rooms &amp; Growing Independence</h4>
                  </div>
                </div>
                <p className="text-xs font-light text-[#5C635A] leading-relaxed mb-5 flex-1">
                  A research-backed guide that reframes the messy room conversation, plus a printable companion tracker to help your teen set their own space goals.
                </p>
                <div className="flex flex-col gap-2">
                  <a href="https://customer-assets.emergentagent.com/job_wellness-journey-231/artifacts/btgcesnl_Messy-Rooms-and-Growing-Independence-Align-Praxis%20%281%29.pdf" target="_blank" rel="noopener noreferrer" className="btn-pill-outline text-[10px] justify-center py-2.5" data-testid="download-messy-rooms-guide">
                    <Download size={12} /> Download Guide
                  </a>
                  <a href="https://customer-assets.emergentagent.com/job_wellness-journey-231/artifacts/g9t40sml_Personal-Space-Goal-Tracker-Align-Praxis.pdf" target="_blank" rel="noopener noreferrer" className="btn-pill-outline text-[10px] justify-center py-2.5" data-testid="download-goal-tracker">
                    <Download size={12} /> Download Tracker
                  </a>
                </div>
              </motion.div>

              {/* Tool 2: Building the Bridge */}
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }} className="glass-card p-7 flex flex-col" data-testid="free-tool-building-bridge">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-2xl bg-[#4A6741]/10 flex items-center justify-center shrink-0">
                    <Compass size={18} className="text-[#4A6741]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#4A6741] mb-1">59-Page Reflection Workbook</p>
                    <h4 className="font-['Cormorant_Garamond'] text-xl font-normal text-[#1A1C19] leading-tight">Building the Bridge</h4>
                  </div>
                </div>
                <p className="text-xs font-light text-[#5C635A] leading-relaxed mb-5 flex-1">
                  A reflection workbook for parents raising children with executive function differences. 59 pages of scaffolding strategies, reflection prompts, and practical frameworks.
                </p>
                <div className="flex flex-col gap-2">
                  <a href="https://customer-assets.emergentagent.com/job_wellness-journey-231/artifacts/ausuzf4c_Building_the_Bridge_ALIGN_Praxis.pdf" target="_blank" rel="noopener noreferrer" className="btn-pill text-[10px] justify-center py-2.5" data-testid="download-building-bridge">
                    <Download size={12} /> Download PDF
                  </a>
                  <a href="/tools/building-the-bridge/preview" className="btn-pill-outline text-[10px] justify-center py-2.5" data-testid="preview-building-bridge">
                    Preview Online
                  </a>
                </div>
              </motion.div>
            </div>
          </section>

          {/* Back */}
          <div className="pb-16 text-center">
            <button onClick={() => navigate("/")} className="text-xs text-[#5C635A] hover:text-[#4A6741] transition-colors inline-flex items-center gap-2">
              <ArrowLeft size={12} /> Back to ALIGN&bull;
            </button>
          </div>
        </main>
      </div>
    </>
  );
}
